
import { api } from "@/api/api";
import BaseLayout from "@/components/shared/BaseLayout.vue";
import { ApiGetWorkingHourDto } from "@/api/generated/Api";
import BaseTooltipIconButton from "@/components/shared/button/BaseTooltipIconButton.vue";
import BaseTableFiltered from "@/components/shared/table/BaseTableFiltered.vue";
import { NotificationItemType } from "@/shared/enums/notificationItemEnum";
import { SingleCourseRouteNames } from "@/shared/enums/RouteNames/singleCourseRouteNames.enum";
import { formatLocalizedDate } from "@/shared/helpers/dateHelpers";
import { globalLoadingWrapper, onParamChange } from "@/shared/helpers/loadingHelpers";
import { openNotification } from "@/shared/helpers/store.helpers";
import { useRoute, useRouter, useStore } from "@/shared/useHelpers";
import { StoreState } from "@/store/store.state.interface";
import { computed, defineComponent, ref } from "@vue/composition-api";
import { WorkingHourStatusEnum } from "@/shared/enums/WorkingHourStatus.enum";
import BaseModal from "@/components/shared/BaseModal.vue";
import RegisterHoursModal from "@/components/course/resources/RegisterHoursModal.vue";
import { useNavigateBack } from "@/shared/helpers/navigationHelpers";

export default defineComponent({
  name: "CourseWorkingHoursPage",
  components: { BaseTableFiltered, BaseTooltipIconButton, BaseModal, RegisterHoursModal, BaseLayout },
  setup() {
    const store = useStore<StoreState>();
    const route = useRoute();
    const router = useRouter();
    const course = computed(() => store.state.courses.course);
    const search = ref("");
    const workingHours = ref<ApiGetWorkingHourDto[]>([]);
    const selectedRows = ref<ApiGetWorkingHourDto[]>([]);

    const selectedRow = ref<ApiGetWorkingHourDto>();

    const displayWorkingHourEntry = (workingHour: ApiGetWorkingHourDto) => {
      router.push({
        name: SingleCourseRouteNames.CourseWorkingHourDetails,
        params: {
          id: route.params.id,
          hourEntryId: workingHour.id.toString(),
          isExternalResource: "courseContractId" in workingHour ? "true" : "false",
        },
      });
    };

    const loadWorkingHours = async () => {
      const getWorkingHoursResponse = (await api.economy.getWorkingHoursAsync({ CourseId: +route.params.id })).data;
      workingHours.value = getWorkingHoursResponse.map((hourRegistered) => ({
        ...hourRegistered,
        isSelectable: hourRegistered.status === WorkingHourStatusEnum.NotApproved,
      }));
    };

    onParamChange(loadWorkingHours);

    const approveSelectedWorkingHourEntries = async () => {
      await globalLoadingWrapper({ blocking: true }, async () => {
        if (!selectedRows.value) {
          return;
        }
        const idsToBeApproved = selectedRows.value.map((hourRegistered) => hourRegistered.id);
        await api.economy.approveWorkingHourAsync(idsToBeApproved);
        openNotification(store, NotificationItemType.Success, "Arbeidstimer godkjent");
      });
      selectedRows.value = [];
      await loadWorkingHours();
    };

    const closeWorkingHoursModal = () => (selectedRow.value = undefined);

    const updateWorkingHours = (workingHour: ApiGetWorkingHourDto) => {
      selectedRow.value = workingHour;
    };

    return {
      course,
      headers,
      search,
      workingHours,
      selectedRow,
      selectedRows,
      loadWorkingHours,
      closeWorkingHoursModal,
      formatLocalizedDate,
      displayWorkingHourEntry,
      approveSelectedWorkingHourEntries,
      updateWorkingHours,
      navigateToCourseDashboard: () => useNavigateBack(SingleCourseRouteNames.CourseDashboard),
    };
  },
});

const headers = [
  { text: "Navn", value: "userFullName" },
  { text: "Handlinger", value: "actions" },
  { text: "Kontrakt-ID", value: "courseContractId" },
  { text: "Type timer", value: "costTypeName" },
  { text: "Antall timer", value: "amount" },
  { text: "Dato", value: "date" },
  { text: "Status", value: "statusText" },
  { text: "Kommentar", value: "comment" },
];
